import clsx from "clsx";
import Button from "components/atoms/buttons/Button";
import { CloseIcon, DateIcon, TimeIcon } from "components/atoms/Icon";
import Modal from "components/atoms/modal";
import VideoPreviewer from "components/atoms/VideoPreviewer";
import moment from "moment";
import { useFormContext } from "react-hook-form";
import {
  isMediaOfTypeAudioOrVideo,
  isMediaOfTypeDocument,
  isMediaOfTypeImage,
  isMediaOfTypeVideo,
} from "utils/helpers";
import { downloadOnClick } from "utils";
import { DATE_FORMAT, TIME_FORMAT } from "../../constants";

type Props = {
  open: boolean;
  onClose: () => void;
  magnet: BrandMagnetDetails;
  handlePublishMemento: () => void;
  disabled?: boolean;
};

function DateTimePreview({ pText, sText, Icon }: { pText: string; sText: string; Icon: JSX.Element }) {
  return (
    <div className="space-y-1 rounded-lg bg-secondary px-6 py-2 text-center ">
      <div className="flex items-center justify-center space-x-1">
        {Icon}
        <div className="text-xxs font-semibold opacity-60">{pText}</div>
      </div>
      <div className="leading-2 text-xs font-bold ">{sText}</div>
    </div>
  );
}

export function MementoModalUI({
  disabled = false,
  open,
  onClose,
  magnetImage,
  magnetName,
  brandName,
  isFileAudioOrVideo,
  mediaUrl,
  isPreviewModal,
  mementoName,
  mementoDesc,
  airdropStartTime,
  airdropStartDate,
  handlePublishMemento,
  isActivationTablePreview,
  actionButtons,
  isMagnetView,
  thumbnailUrl,
  isFileImage,
  isFileDocument,
  isFileVideo,
}: (
  | MementoUserPreviewModalProps
  | MementoBrandPreviewModalProps
  | MementoActivationPreviewModalProps
  | MagnetViewProps
) & {
  disabled?: boolean;
  thumbnailUrl?: string;
  isFileDocument?: boolean;
  isFileImage?: boolean;
  isFileVideo?: boolean;
}) {
  return (
    <Modal open={open} onClose={onClose}>
      <div className={clsx("w-[500px] space-y-5 rounded-2xl bg-primary p-5 shadow-modal")}>
        {/* Section 1  */}
        <div className="flex justify-between">
          <div className="flex space-x-3">
            <img
              className="aspect-1 h-16 rounded-lg border border-white object-cover"
              src={magnetImage ?? undefined}
              alt={magnetName}
            />
            <div className="flex flex-col font-satoshi text-white">
              <span className="text-xl font-semibold leading-tight">{magnetName}</span>
              <span className="opacity-70">{brandName}</span>
            </div>
          </div>
          <button type="button" className="self-start p-1" disabled={disabled} onClick={onClose}>
            <CloseIcon className="h-5 w-5 text-white" />
          </button>
        </div>
        {/* Section 2 */}
        <div className="mx-auto flex h-max flex-col items-center font-satoshi text-white">
          <div className="grid max-h-72 w-10/12 place-items-stretch">
            {/* Octagon */}
            {/* <div className="absolute rounded-xl">
              <div className="h-72 w-72 overflow-hidden  ">
                <div className="h-full w-full rotate-45 rounded-xl">
                  {mediaHash && (
                    <Blurhash
                      className=""
                      hash={mediaHash}
                      width="100%"
                      height="100%"
                      resolutionX={100}
                      resolutionY={100}
                      punch={1}
                    />
                  )}
                </div>
              </div>
            </div> */}
            {/* <div className={clsx("z-50 flex h-72 w-72 items-center justify-center ", isFileVideo && "-mt-4")}> */}
            {/* TODO: Add logic for Preview Video  */}
            {isFileAudioOrVideo ? (
              <div className="h-72">
                <VideoPreviewer
                  videoPlayerId="memento_video_viewer"
                  src={mediaUrl}
                  thumbnailUrl={isFileVideo ? "" : thumbnailUrl}
                />
              </div>
            ) : (
              <img
                className="mx-auto h-full max-h-72 w-auto rounded-2xl border border-white object-scale-down"
                src={isFileImage ? mediaUrl : thumbnailUrl}
                alt={mementoName}
              />
            )}
          </div>
          {isFileDocument && (
            <button
              onClick={() => downloadOnClick(mediaUrl)}
              disabled={disabled}
              type="button"
              className="mt-3 block rounded-full bg-accent bg-opacity-90 px-4 py-2.5 text-xxs font-semibold text-primary shadow-sm hover:bg-opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-40"
            >
              View Content
            </button>
          )}
          <span
            className={clsx(
              isMagnetView ? "mt-2 text-center text-xl font-semibold" : "my-3 text-center text-lg font-450 ",
            )}
          >
            {mementoName}
          </span>
          {(isPreviewModal || isActivationTablePreview) && (
            <div className="flex w-full justify-center space-x-2 text-white ">
              <DateTimePreview Icon={<DateIcon />} pText="Scheduled Date" sText={airdropStartDate} />
              <DateTimePreview Icon={<TimeIcon />} pText="Schedule Time" sText={airdropStartTime} />
            </div>
          )}
          <span className="my-3 mb-6 line-clamp-3 text-center font-450">{mementoDesc}</span>
          {/* Buttons */}
          {isPreviewModal && (
            <div className="mb-4 flex space-x-4 py-2">
              <Button
                text="CANCEL"
                disabled={disabled}
                additionalClasses="sm:px-14"
                variant="whiteOutline"
                onClick={onClose}
              />
              <Button
                disabled={disabled}
                text="SCHEDULE"
                additionalClasses="sm:px-14"
                variant="magnetiqSolid"
                onClick={handlePublishMemento}
              />
            </div>
          )}

          {/* Shown on User Public Profile  */}
          {/* {!isPreviewModal && !isActivationTablePreview && (
            <div className="flex h-12 space-x-4">
              <Button
                text="MORE DETAILS"
                disabled={isPublic}
                additionalClasses="sm:px-14"
                variant="magnetiqSolid"
                onClick={workInProgressAlert}
              />
            </div>
          )} */}

          {(isActivationTablePreview || isMagnetView) && <div className="flex space-x-4">{actionButtons}</div>}
        </div>
      </div>
    </Modal>
  );
}

export default function PreviewMementoModal({ open, onClose, magnet, handlePublishMemento, disabled = false }: Props) {
  const { getValues, watch } = useFormContext<AirDropFormType>();
  // @ts-ignore
  const [mementoName, mementoDesc, mediaType] = getValues(["name", "description", "media_type"]);
  // @ts-ignore
  const airDropDateTime = getValues("start_time");
  const momentDate = moment(airDropDateTime);
  const airdropStartTime = momentDate.format(TIME_FORMAT);
  const airdropStartDate = momentDate.format(DATE_FORMAT);

  // if (!mementoMedia && !existingMediaURL) return null;
  const isFileAuideoOrVideo = !!(watch("media")
    ? isMediaOfTypeAudioOrVideo(mediaType || "")
    : isMediaOfTypeAudioOrVideo((watch("media_url_new") || watch("media_url")) as string));

  const isFileVideo = !!(watch("media")
    ? isMediaOfTypeVideo(mediaType || "")
    : isMediaOfTypeVideo((watch("media_url_new") || watch("media_url")) as string));

  const isFileImage = isMediaOfTypeImage(watch("media_type") as string);
  const isFileDocument = isMediaOfTypeDocument(watch("media_type") as string);

  return (
    <MementoModalUI
      isFileVideo={isFileVideo}
      isFileImage={isFileImage}
      isFileDocument={isFileDocument}
      isFileAudioOrVideo={isFileAuideoOrVideo}
      open={open}
      disabled={disabled}
      onClose={onClose}
      magnetImage={magnet.image_url}
      magnetName={magnet.name}
      brandName={magnet.brand_name}
      mediaUrl={
        watch("media_url_new") || watch("media_url") || watch("thumbnail_url")
          ? watch("media_url") || watch("thumbnail_url")
          : URL.createObjectURL((watch("media") || watch("thumbnail")) as File)
      }
      mementoName={mementoName}
      mementoDesc={mementoDesc}
      isPreviewModal
      airdropStartTime={airdropStartTime}
      airdropStartDate={airdropStartDate}
      handlePublishMemento={handlePublishMemento}
      thumbnailUrl={watch("thumbnail_url") || URL.createObjectURL(watch("thumbnail") as File)}
    />
  );
}

type MementoModalCommonProps = {
  open: boolean;
  onClose: () => void;
  mementoName: string;
  mementoDesc: string;
  magnetImage: string | null;
  magnetName: string;
  brandName: string;
  isFileAudioOrVideo: boolean;
  mediaUrl: string;
  isPublic?: boolean;
};

interface MementoBrandPreviewModalProps extends MementoModalCommonProps {
  isPreviewModal: boolean;
  airdropStartTime: string;
  airdropStartDate: string;
  handlePublishMemento: any;
  actionButtons?: never;
  isActivationTablePreview?: never;
  isMagnetView?: never;
}
interface MementoUserPreviewModalProps extends MementoModalCommonProps {
  isPreviewModal?: never;
  airdropStartTime?: never;
  airdropStartDate?: never;
  handlePublishMemento?: never;
  isActivationTablePreview?: never;
  isMagnetView?: never;
  actionButtons?: never;
}
interface MementoActivationPreviewModalProps extends MementoModalCommonProps {
  isPreviewModal?: never;
  handlePublishMemento?: never;
  airdropStartTime: string;
  airdropStartDate: string;
  isActivationTablePreview: boolean;
  actionButtons: JSX.Element | null;
  isMagnetView?: never;
}

interface MagnetViewProps extends MementoModalCommonProps {
  isMagnetView: boolean;
  isPreviewModal?: never;
  airdropStartTime?: never;
  airdropStartDate?: never;
  handlePublishMemento?: never;
  isActivationTablePreview?: never;
  actionButtons: JSX.Element | null;
}
